import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Card from '../shared/Card';
import SafeIcon from '../../common/SafeIcon';
import api from '../../lib/api';
import * as FiIcons from 'react-icons/fi';

const {
  FiMapPin, FiWifi, FiWifiOff, FiActivity, FiClock, FiEye, FiPhoneCall,
  FiCpu, FiUserCheck, FiChevronRight, FiShield, FiAlertTriangle,
} = FiIcons;

const RISK_STYLES = {
  low: { label: 'LOW', cls: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  moderate: { label: 'MODERATE', cls: 'text-amber-600 bg-amber-50 border-amber-200' },
  high: { label: 'HIGH', cls: 'text-rose-600 bg-rose-50 border-rose-200' },
};

// Deterministic mapping from active alert severity to a patient-facing risk
// label. This is a v1 UI heuristic, not the Deterministic Rules Engine —
// it only summarises alerts that already exist; it does not decide when an
// alert should be created. Flagging for confirmation since even this small
// a threshold is a policy call: critical alert present -> high; else a
// high-severity alert present -> moderate; else low.
function deriveRiskLevel(summary) {
  if (!summary) return null;
  if (summary.critical > 0) return 'high';
  if (summary.high > 0) return 'moderate';
  return 'low';
}

// Deliberately generic, non-alarmist, non-directive copy — this is not
// clinical guidance and shouldn't read as any. It only reflects whether
// the care team has an open alert, never what to do about it.
function guidanceFor(riskLevel, totalActive) {
  if (riskLevel === 'high' || riskLevel === 'moderate') {
    return 'Your care team has been notified and is reviewing.';
  }
  if (totalActive > 0) {
    return 'Your care team is aware and monitoring.';
  }
  return 'No active alerts right now.';
}

function formatTimestamp(iso) {
  if (!iso) return 'Not yet reviewed';
  return new Date(iso).toLocaleString();
}

// Placeholder shape matches SafetySummary in src/contracts/patientIntelligence.js.
// Left unchanged per AbdulRafiu's Option B approval (02/09/2026) — the
// contract itself is not modified. The AI-derived fields it carries
// (safetyScore, aiConfidence, lastReviewedByAI) are no longer rendered as
// real numbers; see AI_FIELDS_AVAILABLE below.
const DEFAULT_SUMMARY = {
  insideSafeZone: true,
  wearableConnected: true,
  lastVitalsUpdate: new Date().toISOString(),
  monitoringActive: true,
  emergencyContactConfigured: true,
  lastClinicianReview: null,
};

// Governance flag: false until Phase 6/7 supplies real, governed AI output.
// While false, the score/confidence block is shown as explicitly
// unavailable rather than populated with any value — per AbdulRafiu's
// instruction that synthetic figures must not sit beside real
// alert-derived state. Flip this once a real AI source is wired in.
const AI_FIELDS_AVAILABLE = false;

/**
 * Personal Safety Summary — patient-facing rollup answering "Am I safe?"
 *
 * v1 (02/09/2026): adds a deterministic section sourced from
 * GET /alerts/summary — risk level derived from active alert severity,
 * active alert count, and generic non-clinical guidance. This section is
 * additive; the SafetySummary contract and its consumers are unchanged.
 * The former AI score/confidence block still renders, but now shows
 * "Not yet available" instead of hardcoded placeholder numbers, so
 * patients are never shown a fabricated score.
 *
 * GeofenceManager is NOT replaced or removed. It continues running as the
 * background service owning live location/geofencing; this card only
 * summarizes its status (insideSafeZone) alongside monitoring, device, and
 * clinician review signals. "View Details" links out to a dedicated
 * Safety/Location page for anyone who wants the full picture.
 *
 * NOTE: the route below ("/patient/safety") does not exist in the router
 * yet — add it alongside whichever page ends up hosting the detailed
 * safety/location view (likely where GeofenceManager's full map lives).
 */
const MySafetyCard = ({ summary = DEFAULT_SUMMARY }) => {
  const [alertSummary, setAlertSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/alerts/summary');
        if (!cancelled) setAlertSummary(res.data);
      } catch (err) {
        if (!cancelled) setError('Unable to load current safety state');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const riskLevel = deriveRiskLevel(alertSummary);
  const risk = riskLevel ? RISK_STYLES[riskLevel] : null;

  const rows = [
    { icon: FiMapPin, label: 'Safe Zone Status', value: summary.insideSafeZone ? 'Inside Safe Zone' : 'Outside Safe Zone' },
    { icon: summary.wearableConnected ? FiWifi : FiWifiOff, label: 'Wearable Connection', value: summary.wearableConnected ? 'Connected' : 'Disconnected' },
    { icon: FiClock, label: 'Last Vitals Update', value: formatTimestamp(summary.lastVitalsUpdate) },
    { icon: FiActivity, label: 'Monitoring Status', value: summary.monitoringActive ? 'Active' : 'Paused' },
    { icon: FiPhoneCall, label: 'Emergency Contact', value: summary.emergencyContactConfigured ? 'Configured' : 'Not set' },
    { icon: FiUserCheck, label: 'Last Clinician Review', value: formatTimestamp(summary.lastClinicianReview) },
  ];

  return (
    <Card title="My Safety">
      {/* Deterministic, alert-derived section — real data, no synthetic values */}
      <div className="mb-5 pb-5 border-b border-gray-100">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Current Safety State</p>
        {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
        {error && <p className="text-sm text-rose-500">{error}</p>}
        {risk && (
          <div className="flex items-center justify-between">
            <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-lg border ${risk.cls}`}>
              <SafeIcon icon={FiShield} className="text-xs" /> {risk.label}
            </span>
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <SafeIcon icon={FiAlertTriangle} className="text-gray-400" />
              {alertSummary.total} active alert{alertSummary.total === 1 ? '' : 's'}
            </span>
          </div>
        )}
        {risk && (
          <p className="text-sm text-gray-600 mt-2">{guidanceFor(riskLevel, alertSummary.total)}</p>
        )}
      </div>

      {/* AI-derived score/confidence — explicitly marked unavailable until
          governed Phase 6/7 output exists. Never populated with a synthetic
          value. */}
      <div className="flex items-center justify-between mb-5 pb-5 border-b border-gray-100">
        <div>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Overall Safety Score</p>
          {AI_FIELDS_AVAILABLE ? (
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-black text-gray-900">{summary.safetyScore}</span>
              <span className="text-sm font-bold text-gray-400">/ 100</span>
            </div>
          ) : (
            <span className="text-sm font-semibold text-gray-400">Not yet available</span>
          )}
        </div>
        <div className="text-right">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">AI Confidence</p>
          <span className="text-sm font-semibold text-gray-400">
            {AI_FIELDS_AVAILABLE ? `${Math.round(summary.aiConfidence * 100)}%` : 'Not yet available'}
          </span>
        </div>
      </div>

      <div className="space-y-3">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center justify-between text-sm">
            <span className="text-gray-500 font-medium flex items-center gap-2">
              <SafeIcon icon={row.icon} className="text-gray-400" /> {row.label}
            </span>
            <span className="font-bold text-gray-900">{row.value}</span>
          </div>
        ))}
      </div>

      <Link
        to="/patient/safety"
        className="mt-5 flex items-center justify-center gap-1 text-xs font-bold text-primary-600 hover:text-primary-500 transition-colors"
      >
        View Details <SafeIcon icon={FiChevronRight} className="text-xs" />
      </Link>
    </Card>
  );
};

export default MySafetyCard;